import React, { useContext, useEffect, useRef, useState } from "react";
import MatrimonialProfileCard from "../../Components/Cards/MatrimonialProfileCard";
import { API_PATHS, errorMessages } from "../../Utils/Constants/constants";
import { getApiResponse, postData } from "../../Utils/AxiosRequest";
import ConditionalLoader from "../../Components/ConditionalLoader";
import ViewProfilePopup from "../../Components/ViewProfilePopup";
import SessionTimoutPopup from "../../Components/SessionTimoutPopup";
import { useForm } from "react-hook-form";
import { Accordion, Button, Card } from "react-bootstrap";
import { checkOnlyNumber } from "../../Utils/validation/validators";
import { MemberDetails } from "../../Contexts/MemberDetailContext";
import { useOutletContext } from "react-router-dom";
import { useAccordionButton } from "react-bootstrap";

function ContextAwareToggle({
  children,
  eventKey,
  callback,
  handleClearFilters,
  isSubmitting,
}) {
  const decoratedOnClick = useAccordionButton(
    eventKey,
    () => callback && callback(eventKey)
  );

  return (
    <div className="d-flex">
      <button
        type="button"
        className="btn mr-2"
        onClick={handleClearFilters}
        disabled={isSubmitting}
      >
        Clear Filters
      </button>
      <button
        type="button"
        onClick={decoratedOnClick}
        className="btn green-btn px-4 py-2 filter-btn"
      >
        <i className="fas fa-filter"></i> Filter
      </button>
    </div>
  );
}

const MatrimonialDashboard = () => {
  const { memberDetails } = useContext(MemberDetails);
  const { updateToastMessage } = useOutletContext();
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(true);
  const [nationalities, setNationalities] = useState([]);
  const [genders, setGenders] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [maritalStatus, setMaritalStatus] = useState([]);
  const [religions, setReligions] = useState([]);
  const [bodyTypes, setBodyTypes] = useState([]);
  const [skinColor, setSkinColor] = useState([]);
  const [occupation, setOccupation] = useState([]);
  const [motherTounge, setMotherTounge] = useState([]);
  const [profileCatagory, setProfileCatagory] = useState([]);
  const [annualIncome, setAnnualIncome] = useState([]);
  const [matrimonialProfiles, setMatrimonialProfiles] = useState([]);
  const [pageError, setError] = useState("");
  const [viewProfile, setViewProfile] = useState({});
  const [profileLoadError, setProfileLoadError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(false);
  const [likedMembers, setLikedMembers] = useState({});
  const [acceptedRequests, setAcceptedRequests] = useState([]);

  const refreshIntervalRef = useRef(null);
  const REFRESH_INTERVAL = 30 * 1000;

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm({
    defaultValues: {
      CityName: "",
      NationalityId: "0",
      GenderId: "0",
      CountryId: "0",
      MaritalStatusId: "0",
      ReligionId: "0",
      CategoryId: "0",
      SubCaste: "",
      BodyTypeId: "0",
      SkinColorId: "0",
      IsPhysicallyDisabled: "0",
    },
  });

  const handleHeartClick = async (memberId, matrimonialDetail) => {
    const isLiking = !matrimonialDetail.isLiked; // Determine if we're liking or disliking
    const payload = {
      MemberId: memberId,
      TargetedMemberMatrimonialId: matrimonialDetail.MemberMatrimonial_Id,
      IsLiked: isLiking, // Use the determined value
      CreatedBy: memberId,
    };

    try {
      const response = await postData(
        `${API_PATHS.UpdateLikeAndDislikeStatus}`,
        payload
      );

      if (response.status === 200 && response.data.IsSuccess) {
        // Show different messages based on like/dislike action
        updateToastMessage(
          isLiking ? "You liked the profile!" : "You disliked the profile",
          isLiking ? "success" : "danger"
        );
        getLikedProfiles();
      } else if (response.status === 401) {
        setSessionTimeout(true);
      } else {
        updateToastMessage(
          response.data?.Message || "Something went wrong.",
          "danger"
        );
      }
    } catch (error) {
      updateToastMessage(
        error.message || "Failed to update like status.",
        "danger"
      );
    }
  };

  const handleRequestFullDetails = async (memberMatrimonialId) => {
    const payload = {
      MemberId: memberDetails.MemberId,
      RequestedMemberMatrimonialId: memberMatrimonialId,
      CreatedBy: memberDetails.MemberId,
    };

    try {
      const response = await postData(
        `${API_PATHS.saveProfileRequest}`,
        payload
      );

      if (response.status === 200 && response.data.IsSuccess) {
        updateToastMessage("Request submitted successfully.", "success");
      } else if (response.status === 401) {
        setSessionTimeout(true);
      } else {
        updateToastMessage(
          response.data?.Message || "Failed to submit request.",
          "danger"
        );
      }
    } catch (error) {
      updateToastMessage(
        error.message || "Failed to submit request.",
        "danger"
      );
    }
  };

  // Add this function to fetch accepted requests
  const getAcceptedRequests = async () => {
    if (!memberDetails?.MemberId) {
      return;
    }

    try {
      const response = await postData(
        `${API_PATHS.GetProfileRequestedByMe}?memberid=${memberDetails.MemberId}`,
        {} // Empty payload since we're using query params
      );

      if (response.status === 200 && response.data?.IsSuccess) {
        // Create a map of all requests for easy lookup
        const requestStatusMap = {};

        response.data.Response?.forEach((request) => {
          requestStatusMap[request.RequestedMemberMatrimonialId] = {
            status: request.DisplayRequestStatus,
            data: request, // Store the full request object if needed
          };
        });

        // Filter out only the approved, pending, and rejected requests
        const requested =
          response.data.Response?.filter(
            (request) =>
              request.DisplayRequestStatus === "Approved" ||
              "Pending" ||
              "Rejected"
            // request.DisplayRequestStatus === "Pending" ||
            // request.DisplayRequestStatus === "Rejected"
          ) || [];

        setAcceptedRequests(requested);
        return requestStatusMap; // Return the full map for other components to use
      } else if (response.status === 401) {
        setSessionTimeout(true);
      } else {
        console.error(
          "Failed to fetch accepted requests:",
          response.data?.Message || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Error fetching accepted requests:", error);
      updateToastMessage("Failed to check request status", "danger");
    }
  };

  const fetchProfiles = async (filterData) => {
    try {
      setIsLoadingProfiles(true);
      setIsSubmitting(true);

      // First API call: filter matrimonial profiles
      const response = await postData(
        API_PATHS.filterMatrimonialProfile,
        filterData ?? {}
      );
      if (response.status === 200 && response.data.IsSuccess) {
        setMatrimonialProfiles(response.data.Response ?? []);
      } else if (response.status === 401) {
        setSessionTimeout(true);
      } else {
        setProfileLoadError(errorMessages.error);
      }
    } catch (error) {
      setProfileLoadError(errorMessages.error);
    } finally {
      setIsLoadingProfiles(false);
      setIsSubmitting(false);
    }
  };

  const handleClearFilters = (e) => {
    e.preventDefault();
    reset({
      CityName: "",
      NationalityId: "0",
      GenderId: "0",
      CountryId: "0",
      MaritalStatusId: "0",
      ReligionId: "0",
      CategoryId: "0",
      SubCaste: "",
      BodyTypeId: "0",
      SkinColorId: "0",
      IsPhysicallyDisabled: "0",
    });
    fetchProfiles({});
  };

  const closeViewProfile = () => {
    setViewProfile((prev) => ({ ...prev, show: false }));
  };

  const getYearDifference = (dateString) => {
    if (!dateString) return "";
    const birthDate = new Date(dateString);
    const ageDifMs = Date.now() - birthDate.getTime();
    const ageDate = new Date(ageDifMs);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  };

  const handleViewProfile = (profile) => {
    setViewProfile({
      show: true,
      ...profile,
      CityName: profile.DistrictName,
      State: profile.StateName,
    });
  };

  const formatReqBody = (data) => {
    const formData = { ...data };
    const reqBody = {};
    Object.keys(formData).forEach((key) => {
      const value = formData[key];
      if (value !== "0" && value?.trim()?.length !== 0) {
        reqBody[key] = value;
      }
    });
    return reqBody;
  };

  const onSubmit = async (data) => {
    const reqBody = formatReqBody(data);
    await fetchProfiles(reqBody);
  };

  const getLikedProfiles = async () => {
    try {
      const res = await postData(
        `${API_PATHS.GetProfileLikeByMe}?memberid=${memberDetails.MemberId}`,
        {}
      );
      if (res.status === 200 && res.data.IsSuccess) {
        const likedProfiles = res.data.Response || [];
        const likedMap = {};
        likedProfiles.forEach((profile) => {
          likedMap[profile.TargetedMemberMatrimonialId] = profile.IsLiked;
        });
        setMatrimonialProfiles((prev) =>
          prev.map((profile) => {
            return {
              ...profile,
              isLiked: likedMap[profile.MemberMatrimonial_Id] || false,
            };
          })
        );
      } else {
        console.warn("Failed to fetch liked profiles:", res.data?.Message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        const [
          nationalitiesResponse,
          genderResponse,
          designationResponse,
          countriesResponse,
          statesResponse,
          districtResponse,
          categoriesResponse,
          maritalStatusResponse,
          bodyTypeResponse,
          relegionResponse,
          skinColorResponse,
          motherToungeResponse,
          annualIncomeResponse,
          occupationResponse,
          profileCatagoryResponse,
        ] = await Promise.all([
          getApiResponse(API_PATHS.getNationalities),
          getApiResponse(API_PATHS.getGenders),
          getApiResponse(API_PATHS.getDesignations),
          getApiResponse(API_PATHS.getCountries),
          getApiResponse(API_PATHS.getStates),
          getApiResponse(API_PATHS.getDisticts),
          getApiResponse(API_PATHS.getCategories),
          getApiResponse(API_PATHS.getMaritalStatus),
          getApiResponse(API_PATHS.getBodyType),
          getApiResponse(API_PATHS.getRelegios),
          getApiResponse(API_PATHS.getComplexions),
          getApiResponse(API_PATHS.getMotherTounges),
          getApiResponse(API_PATHS.getAnnualIncomes),
          getApiResponse(API_PATHS.getOccupations),
          getApiResponse(API_PATHS.getProfileCategories),
        ]);

        if (nationalitiesResponse.status === 200) {
          setNationalities(nationalitiesResponse.data);
        }
        if (genderResponse.status === 200) {
          setGenders(genderResponse.data ?? []);
        }
        if (designationResponse.status === 200) {
          setDesignations(designationResponse.data ?? []);
        }
        if (countriesResponse.status === 200) {
          setCountries(countriesResponse.data ?? []);
        }
        if (statesResponse.status === 200) {
          setStates(statesResponse.data ?? []);
        }
        if (districtResponse.status === 200) {
          setDistricts(districtResponse.data ?? []);
        }
        if (categoriesResponse.status === 200) {
          setCategories(categoriesResponse.data ?? []);
        }
        if (maritalStatusResponse.status === 200) {
          setMaritalStatus(maritalStatusResponse.data ?? []);
        }
        if (bodyTypeResponse.status === 200) {
          setBodyTypes(bodyTypeResponse.data ?? []);
        }
        if (relegionResponse.status === 200) {
          setReligions(relegionResponse.data ?? []);
        }
        if (skinColorResponse.status === 200) {
          setSkinColor(skinColorResponse.data ?? []);
        }
        if (motherToungeResponse.status === 200) {
          setMotherTounge(motherToungeResponse.data ?? []);
        }
        if (annualIncomeResponse.status === 200) {
          setAnnualIncome(annualIncomeResponse.data ?? []);
        }
        if (occupationResponse.status === 200) {
          setOccupation(occupationResponse.data ?? []);
        }
        if (profileCatagoryResponse.status === 200) {
          setProfileCatagory(profileCatagoryResponse.data ?? []);
        }
        await getAcceptedRequests(); // Fetch accepted requests
        await fetchProfiles();
      } catch (error) {
        setError(errorMessages.error);
        console.log(error.response.status, "error response", error);
      } finally {
        setIsLoadingData(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    getLikedProfiles();
  }, [matrimonialProfiles.length]);

  useEffect(() => {
    getAcceptedRequests();
  }, [memberDetails?.MemberId]);

  useEffect(() => {
    // Initial fetch
    getLikedProfiles();
    fetchProfiles();
    getAcceptedRequests();

    // Set up interval for auto-refresh
    refreshIntervalRef.current = setInterval(() => {
      getLikedProfiles();
      fetchProfiles();
      getAcceptedRequests();
    }, REFRESH_INTERVAL);

    // Clean up interval on component unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [memberDetails?.MemberId]);

  return (
    <SessionTimoutPopup isSessionTimout={sessionTimeout}>
      <ConditionalLoader isLoading={isLoadingData} error={pageError}>
        <div className="container">
          <div className="update-profile-body">
            <h2 className="main-title mt-3">Search for a perfect partner</h2>
            <div className="row mb-2">
              <div className="col-sm-12 filter-container">
                <Accordion>
                  <Card>
                    <Card.Header>
                      <div className="d-flex justify-content-end flex-wrap">
                        <form
                          className="d-flex justify-content-start flex-grow-1 mb-2"
                          onSubmit={handleSubmit(onSubmit)}
                        >
                          <div className="form-group mb-0 pr-2">
                            <input
                              type="text"
                              className={`form-control ${
                                errors.CityName && isDirty
                                  ? "red-placeholder border border-danger"
                                  : ""
                              }`}
                              placeholder={
                                errors.CityName && isDirty
                                  ? errors.CityName.message
                                  : "Search by City Name"
                              }
                              autoComplete="off"
                              {...register("CityName")}
                              disabled={isSubmitting}
                            />
                          </div>
                          <button
                            type="submit"
                            style={{
                              height: "fit-content",
                              textWrap: "nowrap",
                            }}
                            className="btn green-btn px-4 py-2 mr-2"
                            disabled={isSubmitting}
                          >
                            Search
                          </button>
                        </form>
                        <ContextAwareToggle
                          handleClearFilters={handleClearFilters}
                          isSubmitting={isSubmitting}
                          eventKey="0"
                        />
                      </div>
                    </Card.Header>
                    <Accordion.Collapse eventKey="0">
                      <Card.Body>
                        <form onSubmit={handleSubmit(onSubmit)}>
                          <div className="row">
                            <div className="col-md-4">
                              <select
                                className="custom-select form-control"
                                {...register("GenderId")}
                                disabled={isSubmitting}
                              >
                                <option value="0">Gender</option>
                                {genders.map((item) => (
                                  <option
                                    key={item.GenderId}
                                    value={item.GenderId}
                                  >
                                    {item.GenderDesc.toUpperCase()}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <select
                                  className="custom-select form-control"
                                  {...register("CurrentDesignationId")}
                                  disabled={isSubmitting}
                                >
                                  <option value="0">Designation</option>
                                  {designations.map((item) => (
                                    <option
                                      key={item.DesignationId}
                                      value={item.DesignationId}
                                    >
                                      {item.DesignationDesc.toUpperCase()}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <select
                                  className="custom-select form-control"
                                  {...register("StateId")}
                                  disabled={isSubmitting}
                                >
                                  <option value="0">State</option>
                                  {states.map((item) => (
                                    <option
                                      key={item.StateId}
                                      value={item.StateId}
                                    >
                                      {item.Name.toUpperCase()}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <input
                                  type="text"
                                  className={`form-control ${
                                    errors.Height && isDirty
                                      ? "red-placeholder border border-danger"
                                      : ""
                                  }`}
                                  placeholder={
                                    errors.Height && isDirty
                                      ? errors.Height.message
                                      : "Height in CM"
                                  }
                                  autoComplete="off"
                                  {...register("Height", {
                                    min: {
                                      value: 50,
                                      message:
                                        "Please enter a valid height in cm",
                                    },
                                    max: {
                                      value: 250,
                                      message:
                                        "Please enter a valid height in cm",
                                    },
                                    validate: (v) =>
                                      checkOnlyNumber(v, "Height"),
                                  })}
                                  disabled={isSubmitting}
                                />
                              </div>
                            </div>
                            <div className="col-md-4">
                              <select
                                className="custom-select form-control"
                                {...register("MaritalStatusId")}
                                disabled={isSubmitting}
                              >
                                <option value="0">Marital Status</option>
                                {maritalStatus.map((item) => (
                                  <option
                                    key={item.MaritalStatusId}
                                    value={item.MaritalStatusId}
                                  >
                                    {item.MaritalStatusDesc.toUpperCase()}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-4">
                              <select
                                className="custom-select form-control"
                                {...register("IsPhysicallyDisabled")}
                                disabled={isSubmitting}
                              >
                                <option value="0">Physically Disabled</option>
                                <option value="1">Yes</option>
                                <option value="2">No</option>
                              </select>
                            </div>
                            <div className="col-md-4">
                              <select
                                className="custom-select form-control"
                                {...register("BodyTypeId")}
                                disabled={isSubmitting}
                              >
                                <option value="0">Body Type</option>
                                {bodyTypes.map((item) => (
                                  <option
                                    key={item.BodyTypeId}
                                    value={item.BodyTypeId}
                                  >
                                    {item.BodyTypeDesc.toUpperCase()}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-4 mb-3">
                              <select
                                className="custom-select form-control"
                                {...register("CategoryId")}
                                disabled={isSubmitting}
                              >
                                <option value="0">Category</option>
                                {categories.map((item) => (
                                  <option
                                    key={item.CategoryId}
                                    value={item.CategoryId}
                                  >
                                    {item.CategoryDesc.toUpperCase()}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-4 mb-3">
                              <select
                                className="custom-select form-control"
                                {...register("ReligionId")}
                                disabled={isSubmitting}
                              >
                                <option value="0">Religion</option>
                                {religions.map((item) => (
                                  <option
                                    key={item.ReligionId}
                                    value={item.ReligionId}
                                  >
                                    {item.ReligionDesc.toUpperCase()}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <select
                                  className="custom-select form-control"
                                  {...register("OccupationId")}
                                  disabled={isSubmitting}
                                >
                                  <option value="0">Occupation</option>
                                  {occupation.map((item) => (
                                    <option
                                      key={item.OccupationId}
                                      value={item.OccupationId}
                                    >
                                      {item.OccupationDesc.toUpperCase()}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <select
                                  className="custom-select form-control"
                                  {...register("Annual_IncomeId")}
                                  disabled={isSubmitting}
                                >
                                  <option value="0">Annual Income</option>
                                  {annualIncome.map((item) => (
                                    <option
                                      key={item.Annual_IncomeId}
                                      value={item.Annual_IncomeId}
                                    >
                                      {item.Annual_IncomeDesc.toUpperCase()}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-12 row">
                            {isDirty &&
                              Object.values(errors).map((error, index) => (
                                <div className="col-sm-6" key={index}>
                                  <p className="text-danger text-left p-0 m-0">{`* ${error.message}`}</p>
                                </div>
                              ))}
                          </div>
                          <div className="d-flex justify-content-between">
                            <button
                              type="button"
                              className="btn"
                              onClick={handleClearFilters}
                              disabled={isSubmitting}
                            >
                              Clear Filters
                            </button>
                            <button
                              type="submit"
                              className="btn green-btn px-4 py-2"
                              disabled={isSubmitting}
                            >
                              Apply Filters
                            </button>
                          </div>
                        </form>
                      </Card.Body>
                    </Accordion.Collapse>
                  </Card>
                </Accordion>
              </div>
            </div>
            <ConditionalLoader
              isLoading={isLoadingProfiles}
              error={profileLoadError}
            >
              <div className="matrimonial-container">
                <div className="row">
                  {matrimonialProfiles.map((data) => (
                    <MatrimonialProfileCard
                      key={data.MemberMatrimonial_Id}
                      age={getYearDifference(data.DOB)}
                      gender={data.Gender}
                      id={data.MemberMatrimonial_Id}
                      imgUrl={data?.DisplayURL}
                      location={data.DistrictName ?? "N/A"}
                      name={`${data.FirstName ?? ""} ${data.LastName ?? ""}`}
                      viewMore={(e) => {
                        e.preventDefault();
                        handleViewProfile(data);
                      }}
                      isLiked={data.isLiked}
                      onLikeToggle={() =>
                        handleHeartClick(memberDetails?.MemberId, data)
                      }
                    />
                  ))}
                </div>
              </div>
              {matrimonialProfiles.length === 0 && (
                <p className="p-4 text-center">
                  <b>Profile not found !!</b>
                </p>
              )}
            </ConditionalLoader>
          </div>
          <ViewProfilePopup
            show={viewProfile.show}
            onClose={closeViewProfile}
            details={viewProfile}
            requestStatus={
              acceptedRequests.find(
                (request) =>
                  request.RequestedMemberMatrimonialId ===
                  viewProfile.MemberMatrimonial_Id
              )?.DisplayRequestStatus
            }
            onRequestDetails={() => {
              handleRequestFullDetails(viewProfile.MemberMatrimonial_Id);
            }}
          />
        </div>
      </ConditionalLoader>
    </SessionTimoutPopup>
  );
};

export default MatrimonialDashboard;
